import { useEffect, useRef } from 'react'

/**
 * Efficiently sets <title>, <meta>, and <link rel="canonical"> tags for
 * SPA route pages. Restores previous values on unmount.
 */
export function usePageMeta(
  title: string,
  description: string,
  canonical: string,
  extra: Partial<{
    ogTitle: string
    ogDesc: string
    ogImage: string
    ogUrl: string
    keywords: string
    twitterTitle: string
    twitterDesc: string
    noindex?: boolean
  }> = {},
) {
  const prev = useRef<{ title: string; tags: string[]; injected: HTMLMetaElement[] } | null>(null)
  useEffect(() => {
    prev.current = { title: document.title, tags: [], injected: [] }
    const updates: Array<{ selector: string; attr: string; value: string }> = [
      { selector: 'meta[name="description"]', attr: 'content', value: description },
      { selector: 'meta[property="og:title"]', attr: 'content', value: extra.ogTitle ?? title },
      { selector: 'meta[property="og:description"]', attr: 'content', value: extra.ogDesc ?? description },
      { selector: 'meta[name="twitter:title"]', attr: 'content', value: extra.twitterTitle ?? title },
      { selector: 'meta[name="twitter:description"]', attr: 'content', value: extra.twitterDesc ?? description },
      { selector: 'link[rel="canonical"]', attr: 'href', value: canonical },
    ]
    if (extra.ogImage) {
      updates.push({ selector: 'meta[property="og:image"]', attr: 'content', value: extra.ogImage })
    }
    if (extra.ogUrl) {
      updates.push({ selector: 'meta[property="og:url"]', attr: 'content', value: extra.ogUrl })
    }
    if (extra.keywords) {
      updates.push({ selector: 'meta[name="keywords"]', attr: 'content', value: extra.keywords })
    }
    document.title = title
    for (const u of updates) {
      const el = document.head.querySelector(u.selector)
      if (el) {
        prev.current.tags.push(`${u.selector}:previous=${el.getAttribute(u.attr) ?? ''}`)
        el.setAttribute(u.attr, u.value)
      } else if (u.selector.startsWith('meta[')) {
        const meta = document.createElement('meta')
        const nameMatch = u.selector.match(/name="([^"]+)"/)
        const propMatch = u.selector.match(/property="([^"]+)"/)
        if (nameMatch?.[1]) meta.name = nameMatch[1]
        if (propMatch?.[1]) meta.setAttribute('property', propMatch[1])
        meta.content = u.value
        document.head.appendChild(meta)
        prev.current.injected.push(meta)
      }
    }
    let injectedNoindex: HTMLMetaElement | null = null
    if (extra.noindex) {
      const existing = document.head.querySelector('meta[name="robots"]')
      if (existing) {
        prev.current.tags.push(`meta[name="robots"]:previous=${existing.getAttribute('content') ?? ''}`)
        existing.setAttribute('content', 'noindex')
      } else {
        injectedNoindex = document.createElement('meta')
        injectedNoindex.name = 'robots'; injectedNoindex.content = 'noindex'
        document.head.appendChild(injectedNoindex)
      }
    }
    return () => {
      if (!prev.current) return
      document.title = prev.current.title
      for (const entry of prev.current.tags) {
        const ci = entry.indexOf(':previous=')
        if (ci === -1) continue
        const sel = entry.slice(0, ci); const val = entry.slice(ci + 10)
        const el = document.head.querySelector(sel)
        if (el) el.tagName.toLowerCase() === 'link' ? el.setAttribute('href', val) : el.setAttribute('content', val)
      }
      if (injectedNoindex) injectedNoindex.remove()
      for (const meta of prev.current.injected) meta.remove()
    }
  }, [title, description, canonical, extra.ogTitle, extra.ogDesc, extra.ogImage, extra.ogUrl, extra.keywords, extra.twitterTitle, extra.twitterDesc, extra.noindex])
}
